import React, { useMemo, useState } from "react";
import axios from "axios";
import { useDbContext } from "../context/DbContext";
import headeLogo from "../assets/images/shersoftnavLogo.png";
import toExcelIcon from "../assets/images/ToExcel.png";
import * as XLSX from "xlsx";

// Search categories/targets (First 3 targets only)
const SEARCH_TARGETS = [
  { id: "Ledger",            label: "Ledger",          category: "Core Data", icon: "📒", desc: "Search accounts by name or code" },
  { id: "schemeRegistrtion", label: "Scheme Account", labelText: "Scheme Account", category: "Core Data", icon: "📋", desc: "Search by scheme account number" },
  { id: "PhoneNo",           label: "Phone Number",    category: "Contact",   icon: "📱", desc: "Search customer by mobile number" },
];

function QuickSearch({ onClose, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();

  const [tableData, setTableData]                 = useState([]);
  const [selectedField, setSelectedField]         = useState("Ledger");
  const [searchTerm, setSearchTerm]               = useState("");
  const [isLoading, setIsLoading]                 = useState(false);
  const [selectedRowIndex, setSelectedRowIndex]   = useState(null);
  const [searchQuery, setSearchQuery]             = useState("");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  const getTableHeaders = useMemo(() => {
    if (tableData.length === 0) return [];
    return Object.keys(tableData[0]);
  }, [tableData]);

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return tableData;
    const q = searchQuery.toLowerCase().trim();
    return tableData.filter((row) =>
      getTableHeaders.some((colKey) => String(row[colKey] ?? "").toLowerCase().includes(q))
    );
  }, [tableData, searchQuery, getTableHeaders]);

  const fetchData = async () => {
    if (!selectedField || !searchTerm.trim()) return;
    setIsLoading(true);
    try {
      const url = `${apiBaseUrl}/main/quick_Search/${selectedField}/${dbCode}?search=${encodeURIComponent(searchTerm)}`;
      const response = await axios.get(url);
      const data = response.data || [];
      setTableData(data);
      setSelectedRowIndex(null);
      if (data.length > 0) {
        setIsSidebarExpanded(false);
      }
    } catch (error) {
      console.error("Error fetching quick search data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchData();
    }
  };

  const exportToExcel = () => {
    if (filteredRows.length === 0) {
      alert("No data available to export!");
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "QuickSearch");
    XLSX.writeFile(workbook, "QuickSearch.xlsx");
  };

  const activeTargetObj = SEARCH_TARGETS.find((t) => t.id === selectedField);
  const inputClass = "w-full px-2.5 text-xs border border-slate-200 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400";

  return (
    <div
      className="bg-[#d2e4fc] w-full h-full rounded-none shadow-2xl flex flex-col items-center absolute left-0 top-0 p-[1%] font-Inter"
      style={{ zIndex }}
    >
      {/* Split View Container */}
      <div className="w-full flex-1 flex gap-3 overflow-hidden relative">

        {/* ── LEFT CARD: Search Parameters Sidebar ─────────────────────────── */}
        <div
          className={`bg-white border border-[#b8cce0] rounded-[10px] shadow-sm h-full transition-all duration-300 flex flex-col shrink-0 overflow-y-auto ${
            isSidebarExpanded ? "w-[310px] px-4 py-4" : "w-0 overflow-hidden border-none shadow-none"
          }`}
        >
          {isSidebarExpanded && (
            <div className="flex flex-col gap-3 font-Inter">
              
              {/* Header Title */}
              <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
                <img src={headeLogo} alt="Logo" className="h-5 w-auto object-contain" />
                <span className="font-bold text-slate-800 text-sm font-Inter">Search Parameters</span>
              </div>

              {/* SEARCH KEYWORD INPUT */}
              <div className="flex flex-col gap-1.5">
                <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">
                  1. ENTER KEYWORD
                </span>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={`Enter ${activeTargetObj ? activeTargetObj.label : "keyword"}...`}
                    className={inputClass}
                    style={{ height: "30px" }}
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="absolute right-2 text-slate-400 hover:text-slate-600 text-xs font-bold"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* TARGET SELECTION */}
              <div className="flex flex-col gap-1.5 pt-2 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold text-slate-400 font-Inter uppercase tracking-wider">
                    2. SELECT SEARCH TARGET
                  </span>
                  <span className="text-[10px] font-semibold text-blue-600 font-Inter">
                    {activeTargetObj?.label}
                  </span>
                </div>

                {/* Target Chips Grid */}
                <div className="flex flex-col gap-2 pt-1">
                  {SEARCH_TARGETS.map((target) => {
                    const isSelected = selectedField === target.id;
                    return (
                      <button
                        key={target.id}
                        onClick={() => setSelectedField(target.id)}
                        className={`w-full flex items-center justify-between p-2.5 rounded-lg border text-left transition-all font-Inter cursor-pointer ${
                          isSelected
                            ? "bg-blue-50 border-blue-500 shadow-xs"
                            : "bg-white border-slate-200 hover:border-blue-300 hover:bg-slate-50/80"
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="text-base leading-none">{target.icon}</span>
                          <div className="flex flex-col min-w-0">
                            <span className={`text-xs font-bold truncate ${isSelected ? "text-blue-900" : "text-slate-700"}`}>
                              {target.label}
                            </span>
                            <span className="text-[10px] text-slate-400 truncate">
                              {target.desc}
                            </span>
                          </div>
                        </div>

                        {isSelected && (
                          <span className="w-2 h-2 rounded-full bg-blue-600 shrink-0 ml-1"></span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-1.5 mt-2 pt-2 border-t border-slate-200">
                <button
                  onClick={fetchData}
                  disabled={!selectedField || !searchTerm.trim() || isLoading}
                  className="w-full bg-[#088395] hover:bg-[#066f7f] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-2 rounded shadow-sm text-xs font-Inter border-none cursor-pointer flex items-center justify-center gap-2 transition-all"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>SEARCHING...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <span>SEARCH NOW</span>
                    </>
                  )}
                </button>

                <button
                  onClick={exportToExcel}
                  className="w-full bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold py-1.5 rounded shadow-sm text-xs font-Inter cursor-pointer flex items-center justify-center gap-2"
                >
                  <img className="h-4 w-auto" src={toExcelIcon} alt="Excel" />
                  EXPORT EXCEL
                </button>
              </div>

            </div>
          )}
        </div>

        {/* Vertical Sidebar Toggle Tab */}
        <div
          onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
          className={`w-[20px] h-[40px] bg-[#1e73be] hover:bg-blue-700 text-white flex items-center justify-center cursor-pointer select-none font-bold shadow-md absolute top-1/2 -translate-y-1/2 z-20 rounded-r transition-all duration-300 ${
            isSidebarExpanded ? "left-[310px]" : "left-0"
          }`}
        >
          {isSidebarExpanded ? "«" : "»"}
        </div>

        {/* ── RIGHT CARD: Search Results Table ───────────────────────────── */}
        <div className="flex-1 h-full bg-white border border-[#b8cce0] rounded-[10px] shadow-sm flex flex-col p-4 overflow-hidden">
          
          {/* Header Row */}
          <div className="flex items-center justify-between pb-3 border-b border-gray-200 mb-3 font-Inter shrink-0">
            <div className="flex items-center gap-3">
              <span className="font-bold text-slate-800 text-lg">Quick Search</span>
              <span className="bg-blue-50 border border-blue-200 text-blue-800 px-2.5 py-0.5 rounded-full text-[11px] font-bold">
                Target: {activeTargetObj?.label}
              </span>
              <span className="bg-slate-100 border border-slate-200 text-slate-700 px-2.5 py-0.5 rounded-full text-[11px] font-semibold">
                Total Records: {filteredRows.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={exportToExcel}
                className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer"
              >
                <img className="h-3.5 w-auto" src={toExcelIcon} alt="Excel" />
                Excel
              </button>

              <button
                onClick={fetchData}
                disabled={!searchTerm.trim()}
                className="bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
              >
                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3-3m0 0l3 3m-3-3v12" />
                </svg>
                Refresh
              </button>
              
              <button
                onClick={onClose}
                className="bg-slate-100 hover:bg-red-50 hover:text-red-600 text-slate-700 p-1.5 rounded text-xs font-bold border-none cursor-pointer flex items-center justify-center ml-1"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Quick Search bar in table */}
          <div className="w-full flex justify-end pb-3 shrink-0">
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search across returned results..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-7 py-1.5 text-xs border border-slate-300 rounded-[5px] focus:outline-none focus:ring-1 focus:ring-blue-500 font-Inter font-semibold text-slate-800 bg-white transition-all placeholder-slate-400 h-[28px]"
              />
              <svg
                className="absolute left-2.5 top-2 h-3.5 w-3.5 text-blue-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600 text-xs font-semibold px-1"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Scrollable Data Table */}
          <div className="w-full flex-1 min-h-0 overflow-auto border border-slate-300 rounded text-slate-800 text-[11px] font-semibold font-Inter">
            {isLoading ? (
              <div className="w-full h-full flex items-center justify-center bg-white/70">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="font-Inter text-xs text-blue-900 font-semibold">Searching Database...</span>
                </div>
              </div>
            ) : tableData.length === 0 ? (
              <div className="w-full h-full flex items-center justify-center bg-white">
                <div className="flex flex-col items-center gap-2 text-center py-12 px-4">
                  <div className="w-12 h-12 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xl font-bold mb-1">
                    🔍
                  </div>
                  <span className="font-bold text-slate-700 text-sm font-Inter">No Data Displayed</span>
                  <span className="text-xs text-slate-400 max-w-sm font-Inter">
                    Select a target, enter a search keyword on the left parameter panel, and click <strong className="text-slate-600">SEARCH NOW</strong>.
                  </span>
                </div>
              </div>
            ) : (
              <div className="min-w-max flex flex-col min-h-full">
                
                {/* Header Row */}
                <div className="sticky top-0 z-10 bg-[#1e73be] border-b border-slate-300 shrink-0">
                  <div className="flex h-8">
                    <div className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center justify-center w-14 shrink-0">
                      Sl No
                    </div>
                    {getTableHeaders.map((headerKey) => (
                      <div
                        key={headerKey}
                        className="font-Inter text-[11px] font-bold text-white border-r border-slate-300 px-3 flex items-center shrink-0 min-w-[150px] flex-1 truncate"
                      >
                        {headerKey}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Data Rows */}
                <div className="bg-white flex-1">
                  {filteredRows.length === 0 ? (
                    <div className="font-Inter text-xs text-gray-400 text-center py-12">
                      No records matching your search query.
                    </div>
                  ) : (
                    filteredRows.map((row, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedRowIndex(index)}
                        className={`flex h-8 border-b border-slate-300 hover:bg-blue-50/40 cursor-pointer ${
                          index === selectedRowIndex
                            ? "bg-blue-100 font-semibold text-blue-900"
                            : index % 2 === 0
                            ? "bg-white"
                            : "bg-[#f2f7ff]"
                        }`}
                      >
                        {/* Sl No */}
                        <div className="font-Inter text-[11px] border-r border-slate-300 px-3 text-slate-700 flex items-center justify-center w-14 shrink-0">
                          {index + 1}
                        </div>

                        {/* Dynamic Columns */}
                        {getTableHeaders.map((headerKey) => {
                          const val = String(row[headerKey] ?? "");
                          const isBlueLink =
                            headerKey.toLowerCase().includes("name") ||
                            headerKey.toLowerCase().includes("code") ||
                            headerKey.toLowerCase().includes("acc");

                          return (
                            <div
                              key={headerKey}
                              className={`font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center shrink-0 min-w-[150px] flex-1 truncate ${
                                isBlueLink
                                  ? "text-[#1e73be] hover:underline font-medium cursor-pointer"
                                  : "text-slate-700"
                              }`}
                            >
                              {val === "null" || val === "-" ? "" : val}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  )}
                </div>

                {/* Sticky Footer Totals */}
                {filteredRows.length > 0 && (
                  <div className="flex h-8 bg-[#dbeafe] text-blue-900 font-bold sticky bottom-0 z-10 border-t border-slate-300 shrink-0">
                    <div className="font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center justify-center w-14 shrink-0">
                      Total
                    </div>
                    <div className="font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center truncate min-w-[150px] flex-1">
                      {filteredRows.length} Records
                    </div>
                    {getTableHeaders.slice(1).map((headerKey) => (
                      <div key={headerKey} className="font-Inter text-[11px] border-r border-slate-300 px-3 flex items-center shrink-0 min-w-[150px] flex-1" />
                    ))}
                  </div>
                )}

              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
}

export default QuickSearch;
